import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Resolver, useForm } from 'react-hook-form';

import { createTaxInfo, getTaxInfo } from '@/api/tax-info';

import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';

import { ForeignIndiciaCountryDetail } from '@/utils/user';

import {
  DEFAULT_FOREIGN_NON_TAX_RESIDENCY_BLOCK,
  shouldShowForeignNonTaxResidencyForm,
} from '../ForeignNonTaxResidencySection/utils';
import {
  DEFAULT_FOREIGN_TAX_RESIDENCY_BLOCK,
  shouldShowForeignTaxResidencyForm,
} from '../ForeignTaxResidencySection/utils';
import { OtherCountriesTaxFormValues } from '../utils/types';
import {
  getOtherCountriesTaxValidationSchema,
  isOtherCountriesTaxFormValid,
} from './schema';
import {
  buildOtherCountriesTaxStatusPayload,
  DEFAULT_OTHER_COUNTRIES_TAX_FORM_VALUES,
  mapTaxInfoToOtherCountriesFormValues,
  shouldSaveNoForeignTaxEntry,
  shouldShowForeignIndiciaWarning,
} from './utils';

type UseOtherCountriesTaxStatusQuestionFormOptions = {
  foreignIndiciaCountries: ForeignIndiciaCountryDetail[];
  tenantCountryCode: string;
  onAnswered: () => void;
};

export function useOtherCountriesTaxStatusQuestionForm({
  foreignIndiciaCountries,
  tenantCountryCode,
  onAnswered,
}: UseOtherCountriesTaxStatusQuestionFormOptions) {
  const queryClient = useQueryClient();
  const hasProfileForeignIndicia = foreignIndiciaCountries.length > 0;

  const validationSchema = useMemo(
    () => getOtherCountriesTaxValidationSchema(foreignIndiciaCountries),
    [foreignIndiciaCountries],
  );

  const { data: taxInfo } = useQuery({
    queryKey: ['taxInfo'],
    queryFn: getTaxInfo,
  });

  const savedFormValues = useMemo(
    () => mapTaxInfoToOtherCountriesFormValues(taxInfo),
    [taxInfo],
  );

  const {
    control,
    watch,
    resetField,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<OtherCountriesTaxFormValues>({
    resolver: yupResolver(
      validationSchema,
    ) as Resolver<OtherCountriesTaxFormValues>,
    defaultValues: DEFAULT_OTHER_COUNTRIES_TAX_FORM_VALUES,
    values: savedFormValues,
    resetOptions: {
      keepDirtyValues: true,
    },
    mode: 'onChange',
  });

  const formValues = watch();
  const hasForeignIndicia = formValues.hasForeignIndicia;
  const isForeignTaxResident = formValues.isForeignTaxResident;

  const canSubmit = isOtherCountriesTaxFormValid(
    formValues,
    foreignIndiciaCountries,
  );

  const showForeignIndiciaWarning = shouldShowForeignIndiciaWarning(
    { hasForeignIndicia, isForeignTaxResident },
    hasProfileForeignIndicia,
  );
  const showForeignTaxResidencyForm = shouldShowForeignTaxResidencyForm({
    hasForeignIndicia,
    isForeignTaxResident,
  });
  const showForeignNonTaxResidencyForm = shouldShowForeignNonTaxResidencyForm({
    hasForeignIndicia,
    isForeignTaxResident,
  });

  useEffect(() => {
    if (!showForeignTaxResidencyForm) {
      return;
    }

    const currentResidencies = getValues('foreignTaxResidencies');

    if (!currentResidencies?.length) {
      setValue(
        'foreignTaxResidencies',
        [{ ...DEFAULT_FOREIGN_TAX_RESIDENCY_BLOCK }],
        {
          shouldValidate: true,
        },
      );
    }
  }, [showForeignTaxResidencyForm, getValues, setValue]);

  useEffect(() => {
    if (!showForeignNonTaxResidencyForm) {
      return;
    }

    const currentResidencies = getValues('foreignNonTaxResidencies');

    if (!currentResidencies?.length) {
      setValue(
        'foreignNonTaxResidencies',
        [{ ...DEFAULT_FOREIGN_NON_TAX_RESIDENCY_BLOCK }],
        {
          shouldValidate: true,
        },
      );
    }
  }, [showForeignNonTaxResidencyForm, getValues, setValue]);

  const {
    mutate: saveTaxInfoAnswer,
    isPending,
    error,
  } = useMutationWithUserData({
    mutationFn: (values: OtherCountriesTaxFormValues) => {
      const payload = buildOtherCountriesTaxStatusPayload(
        values,
        tenantCountryCode,
        foreignIndiciaCountries,
        taxInfo,
        hasProfileForeignIndicia,
      );
      return createTaxInfo(payload);
    },
    onFinished: () => {
      queryClient.invalidateQueries({ queryKey: ['taxInfo'] });
      onAnswered();
    },
  });

  const onSubmit = handleSubmit(values => {
    if (shouldSaveNoForeignTaxEntry(values)) {
      saveTaxInfoAnswer(values);
      return;
    }

    if (shouldShowForeignTaxResidencyForm(values)) {
      saveTaxInfoAnswer(values);
      return;
    }

    if (shouldShowForeignNonTaxResidencyForm(values)) {
      saveTaxInfoAnswer(values);
      return;
    }

    onAnswered();
  });

  return {
    control,
    errors,
    canSubmit,
    isPending,
    error,
    onSubmit,
    resetField,
    hasForeignIndicia,
    isForeignTaxResident,
    showForeignIndiciaWarning,
    showForeignTaxResidencyForm,
    showForeignNonTaxResidencyForm,
  };
}
